// Fallback voice input through the browser's Web Speech API (SpeechRecognition).
// Needs no API key or server. It only transcribes; the game parses onFinal text with
// its command grammar. Chrome and Edge send audio to their own cloud recognizer.
//
// Callback contract: onState fires on real changes only, and nothing fires after
// stop() returns (pending results are discarded, so a half-spoken command never runs).

// Minimal local types: lib.dom doesn't reliably declare SpeechRecognition.
interface RecognitionAlternative {
  transcript: string;
}
interface RecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  readonly [index: number]: RecognitionAlternative | undefined;
}
interface RecognitionResultEvent {
  readonly resultIndex: number;
  readonly results: { readonly length: number; readonly [index: number]: RecognitionResult | undefined };
}
interface RecognitionErrorEvent {
  readonly error: string;
  readonly message?: string;
}
interface Recognizer {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onstart: (() => void) | null;
  onresult: ((event: RecognitionResultEvent) => void) | null;
  onerror: ((event: RecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type RecognizerCtor = new () => Recognizer;

export interface BrowserVoiceCallbacks {
  onPartial(text: string): void;
  onFinal(text: string): void;
  onState(listening: boolean, error?: string): void;
}

/** Errors that mean listening can't work until the user changes something. */
const FATAL_ERRORS: Record<string, string> = {
  'not-allowed': 'Microphone permission denied',
  'service-not-allowed': 'Speech recognition is blocked in this browser',
  'audio-capture': 'No microphone found',
  'language-not-supported': 'English speech recognition is not available in this browser',
};
const RESTART_DELAY_MS = 150;
const RESTART_WINDOW_MS = 15_000;
const MAX_RESTARTS_IN_WINDOW = 8;

function recognizerCtor(): RecognizerCtor | null {
  const g = globalThis as unknown as { SpeechRecognition?: RecognizerCtor; webkitSpeechRecognition?: RecognizerCtor };
  return g.SpeechRecognition ?? g.webkitSpeechRecognition ?? null;
}

export function browserVoiceSupported(): boolean {
  return recognizerCtor() !== null;
}

export class BrowserVoice {
  private readonly cb: BrowserVoiceCallbacks;
  private rec: Recognizer | null = null;
  private wanted = false;
  private on = false;
  private running = false;
  private interim = '';
  private lastError: string | null = null;
  private restarts: number[] = [];
  private restartTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(cb: BrowserVoiceCallbacks) {
    this.cb = cb;
  }

  get listening(): boolean {
    return this.on;
  }

  start(): void {
    if (this.wanted) return;
    const Ctor = recognizerCtor();
    if (!Ctor) {
      this.emitState(false, 'Speech recognition is not supported in this browser');
      return;
    }
    this.wanted = true;
    this.restarts = [];
    this.lastError = null;
    this.rec = this.create(Ctor);
    this.launch();
  }

  stop(): void {
    if (!this.wanted && !this.rec) return;
    this.wanted = false;
    this.release();
    this.interim = '';
    if (this.on) {
      this.on = false;
      this.emitState(false);
    }
  }

  private create(Ctor: RecognizerCtor): Recognizer {
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';
    rec.maxAlternatives = 1;
    rec.onstart = () => {
      if (rec !== this.rec || this.on) return;
      this.on = true;
      this.emitState(true);
    };
    rec.onresult = (event) => this.onResult(rec, event);
    rec.onerror = (event) => this.onError(rec, event);
    rec.onend = () => this.onEnd(rec);
    return rec;
  }

  private launch(): void {
    const rec = this.rec;
    if (!rec || !this.wanted) return;
    try {
      rec.start();
      this.running = true;
    } catch (err) {
      if (err instanceof Error && err.name === 'InvalidStateError') {
        this.running = true; // already running
        return;
      }
      this.fail(`Speech recognition could not start: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  private onResult(rec: Recognizer, event: RecognitionResultEvent): void {
    if (rec !== this.rec) return;
    let interim = '';
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const result = event.results[i];
      const transcript = result?.[0]?.transcript ?? '';
      if (!result) continue;
      if (result.isFinal) {
        const text = transcript.trim();
        if (text) {
          this.interim = '';
          this.safely(() => this.cb.onFinal(text));
        }
      } else {
        interim += transcript;
      }
    }
    const shown = interim.trim();
    if (shown && shown !== this.interim) {
      this.interim = shown;
      this.safely(() => this.cb.onPartial(shown));
    }
  }

  private onError(rec: Recognizer, event: RecognitionErrorEvent): void {
    if (rec !== this.rec) return;
    const fatal = FATAL_ERRORS[event.error];
    if (fatal) {
      this.fail(fatal);
      return;
    }
    // no-speech (silence), aborted and network end the session; onEnd restarts it.
    this.lastError = event.error;
    if (event.error !== 'no-speech' && event.error !== 'aborted') {
      console.warn('[voice] speech recognition error:', event.error, event.message ?? '');
    }
  }

  private onEnd(rec: Recognizer): void {
    if (rec !== this.rec) return;
    this.running = false;
    if (this.interim) {
      // An unfinished phrase is dropped rather than run as a command; clear its caption.
      this.interim = '';
      this.safely(() => this.cb.onPartial(''));
    }
    if (!this.wanted) return;
    // Chrome ends continuous recognition after silence or about a minute. Keep going,
    // but give up if it keeps dying (offline, service down).
    const now = Date.now();
    this.restarts = this.restarts.filter((t) => now - t < RESTART_WINDOW_MS);
    this.restarts.push(now);
    if (this.restarts.length > MAX_RESTARTS_IN_WINDOW) {
      this.fail(this.lastError === 'network' ? 'Speech recognition lost its network connection' : 'Speech recognition keeps stopping');
      return;
    }
    this.restartTimer = setTimeout(() => {
      this.restartTimer = null;
      this.launch();
    }, RESTART_DELAY_MS * this.restarts.length);
  }

  private fail(message: string): void {
    this.wanted = false;
    this.release();
    this.interim = '';
    this.on = false;
    this.emitState(false, message);
  }

  /** Detaches and aborts the recognizer so no late events reach the callbacks. */
  private release(): void {
    if (this.restartTimer) clearTimeout(this.restartTimer);
    this.restartTimer = null;
    const rec = this.rec;
    this.rec = null;
    if (!rec) return;
    rec.onstart = null;
    rec.onresult = null;
    rec.onerror = null;
    rec.onend = null;
    if (this.running) {
      try {
        rec.abort();
      } catch {
        // not running
      }
    }
    this.running = false;
  }

  private emitState(listening: boolean, error?: string): void {
    this.safely(() => this.cb.onState(listening, error));
  }

  private safely(fn: () => void): void {
    try {
      fn();
    } catch (err) {
      console.error('[voice] callback threw', err);
    }
  }
}
